// dependencies
import React from "react";
import moment from "moment";
import { Link } from "yarrr";
import PropTypes from "prop-types";
import { Map, List } from "immutable";
import { connect } from "react-redux";

// config
import config from "./config";

// components
import Button from "Components/Button";
import Paginator from "Components/Paginator";
import AsyncSelect from "react-select/async";
import TextInput from "Components/Inputs/Text";
import PageSpinner from "Components/Spinner/PageSpinner";
import CustomUserOption from "Components/CustomSelectOptions/User";
import CustomCompanyOption from "Components/CustomSelectOptions/Company";

// actions
import {
	init,
	searchSnapshots,
	updateInput,
	searchUsers,
	searchCompanies,
	updateReportIDInput,
	changePage,
} from "./actions";

// selectors
import { getIsRequesting } from "Selectors/app";
import { getReviewReports, getReportID, getCurrentPage } from "./selectors";

// styles
import css from "./ReviewSnapshots.styl";

class ReviewSnapshots extends React.PureComponent {
	static propTypes = {
		isFetching: PropTypes.bool.isRequired,
		init: PropTypes.func.isRequired,
		changePage: PropTypes.func.isRequired,
		searchSnapshots: PropTypes.func.isRequired,
		updateInput: PropTypes.func.isRequired,
		searchUsers: PropTypes.func.isRequired,
		searchCompanies: PropTypes.func.isRequired,
		updateReportIDInput: PropTypes.func.isRequired,
		currentPage: PropTypes.number.isRequired,
		environment: PropTypes.string.isRequired,
		reportID: PropTypes.string.isRequired,
		reviewReports: PropTypes.instanceOf(List).isRequired,
	};

	componentDidMount = () => {
		this.props.init();
	};

	componentDidUpdate = (prevProps) => {
		if (prevProps.environment !== this.props.environment) {
			this.props.init();
		}
	};

	handleInputKeyDown = (event) => {
		if (event.key === "Enter") {
			this.handleSearch();
		}
	};

	handleSearch = () => {
		this.props.searchSnapshots();
	};

	handleInputChange = (name, value) => {
		this.props.updateInput(name, value);
	};

	handleAuthorChange = (value, changeType) => {
		if (config.searchableActions.indexOf(changeType.action) >= 0) {
			this.props.updateInput("author", Map(value));
		}
	};

	handleCompanyChange = (value, changeType) => {
		if (config.searchableActions.indexOf(changeType.action) >= 0) {
			this.props.updateInput("company", Map(value));
		}
	};

	handleReportIDChange = (value) => {
		this.props.updateReportIDInput(value);
	};

	handlePageChange = (nextPage) => {
		this.props.changePage(nextPage);
	};

	loadUserOptions = (value) => this.props.searchUsers(value);

	loadCompanyOptions = (value) => this.props.searchCompanies(value);

	render() {
		const { reviewReports, reportID, currentPage, isFetching } = this.props;

		return (
			<div className={css.main}>
				<div className={css.container}>
					<div>
						<div className={css.filtersContainer}>
							<div className={`${css.filterBox}`}>
								<div className={css.label}>Author</div>
								<AsyncSelect
									isClearable
									cacheOptions
									defaultOptions
									escapeClearsValue
									loadOptions={this.loadUserOptions}
									onChange={this.handleAuthorChange}
									components={{ Option: CustomUserOption }}
								/>
							</div>

							<div className={`${css.filterBox}`}>
								<div className={css.label}>Company</div>
								<AsyncSelect
									isClearable
									cacheOptions
									defaultOptions
									escapeClearsValue
									onChange={this.handleCompanyChange}
									loadOptions={this.loadCompanyOptions}
									components={{ Option: CustomCompanyOption }}
								/>
							</div>
						</div>

						<div className={`${css.filtersContainer} ${css.reportFilter}`}>
							<div className={css.orLabel}>Or, search by Report ID</div>

							<div className={`${css.filterBox}`}>
								<div className={css.label}>Review Report ID</div>
								<TextInput onChange={this.handleReportIDChange} value={reportID} />
							</div>

							<div className={`${css.filterBox}`}>
								<div className={css.label} />
								<Button onClick={this.props.searchSnapshots}>Search</Button>
							</div>
						</div>
					</div>

					<div className={css.reviewReports}>
						<div className={css.reviewReportTable}>
							<div className={css.tableHead}>
								<div className={`${css.headCell}`} />
								<div className={`${css.headCell}`}>Author</div>
								<div className={`${css.headCell}`}>Company</div>
								<div className={`${css.headCell}`}>Review</div>
								<div className={`${css.headCell}`}>Review Report</div>
								<div className={`${css.headCell}`}>Subject</div>
								<div className={`${css.headCell}`}>Date</div>
							</div>

							<div className={css.tableBody}>
								{reviewReports.map((report) => (
									<div key={report.get("_id")} className={css.reviewReport}>
										<div className={`${css.bodyCell}`}>
											<Link
												href={`/review-snapshots/${report.getIn([
													"review_report",
													"id",
												])}/${report.getIn(["author", "id"])}/`}
											>
												<div className={css.viewButton}>View</div>
											</Link>
										</div>
										<div className={`${css.bodyCell}`}>{report.getIn(["author", "name"])}</div>
										<div className={`${css.bodyCell}`}>{report.getIn(["company", "name"])}</div>
										<div className={`${css.bodyCell}`}>{report.getIn(["review", "name"])}</div>
										<div className={`${css.bodyCell}`}>
											{report.getIn(["review_report", "name"])}
										</div>
										<div className={`${css.bodyCell}`}>{report.getIn(["subject", "name"])}</div>
										<div className={`${css.bodyCell}`}>
											{report.get("date")
												? moment(report.get("date")).format("MMM D YYYY h:mm a")
												: null}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>

					<div className={css.paginator}>
						<Paginator
							isRequesting={isFetching}
							onClick={this.handlePageChange}
							pageCount={1000}
							currentPage={currentPage}
						/>
					</div>

					{isFetching ? <PageSpinner /> : null}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => ({
	reportID: getReportID(state),
	currentPage: getCurrentPage(state),
	reviewReports: getReviewReports(state),
	isFetching: getIsRequesting(state, "REVIEWREPORTSNAPSHOTS:GET"),
});

const mapDispatchToProps = (dispatch) => ({
	init: () => dispatch(init()),
	searchSnapshots: () => dispatch(searchSnapshots()),
	searchUsers: (value) => dispatch(searchUsers(value)),
	searchCompanies: (value) => dispatch(searchCompanies(value)),
	changePage: (nextPage) => dispatch(changePage(nextPage)),
	updateReportIDInput: (value) => dispatch(updateReportIDInput(value)),
	updateInput: (name, value) => dispatch(updateInput(name, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewSnapshots);
